import { CameraOutlined } from '@ant-design/icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ScopeButton } from '@monorepo/shared-lib';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';

export type WebcamCaptureModalProps = {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
};

export const WebcamCaptureModal: React.FC<WebcamCaptureModalProps> = ({
  open,
  onClose,
  onCapture,
}) => {
  const { t } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [ready, setReady] = React.useState(false);

  useOverlayBackHandler(open, onClose, 'webcam-capture-modal', pwaMobileNav);

  const stopStream = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  React.useEffect(() => {
    if (!open) {
      stopStream();
      setError(null);
      setReady(false);
      return;
    }

    let cancelled = false;

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t('organisation.receipts-camera-unavailable'));
        return;
      }

      try {
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
        }
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setReady(true);
      } catch {
        if (!cancelled) {
          setError(t('organisation.receipts-camera-permission'));
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, stopStream, t]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleCapture = React.useCallback(() => {
    const video = videoRef.current;
    if (!video || !ready) {
      return;
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    context.drawImage(video, 0, 0, width, height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        const file = new File([blob], `scan-${stamp}.jpg`, {
          type: 'image/jpeg',
        });
        onCapture(file);
        onClose();
      },
      'image/jpeg',
      0.92
    );
  }, [onCapture, onClose, ready]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="scope-webcam-capture"
      role="dialog"
      aria-modal="true"
      aria-labelledby="scope-webcam-capture-title"
    >
      <button
        type="button"
        className="scope-webcam-capture__backdrop"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div className="scope-webcam-capture__sheet">
        <h2
          id="scope-webcam-capture-title"
          className="scope-webcam-capture__title"
        >
          {t('organisation.receipts-take-photo')}
        </h2>
        {error ? (
          <p className="scope-webcam-capture__error">{error}</p>
        ) : (
          <video
            ref={videoRef}
            className="scope-webcam-capture__video"
            autoPlay
            playsInline
            muted
          />
        )}
        <div className="scope-webcam-capture__actions">
          <ScopeButton type="default" onClick={onClose}>
            {t('common.cancel')}
          </ScopeButton>
          <ScopeButton
            type="primary"
            icon={<CameraOutlined />}
            onClick={handleCapture}
            disabled={!ready || !!error}
          >
            {t('organisation.receipts-capture-shot')}
          </ScopeButton>
        </div>
      </div>
    </div>
  );
};

export default WebcamCaptureModal;
